import * as Yup from 'yup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { MdRequestQuote } from 'react-icons/md';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Field from 'legacy/common/components/wysiwyg-form/Field';

interface TaxModalProps {
  initialValues: { taxId: string; name: string; ratePercent: number };
  onClose: () => void;
  onSave: (values?: {
    taxId: string;
    name: string;
    ratePercent: number;
  }) => void;
}

const TaxSchema = Yup.object().shape(
  {
    taxId: Yup.string()
      .min(9)
      .label('Tax ID')
      .when('name', {
        is: (name: string) => !!name,
        then: (schema) => schema.required('Required')
      }),
    name: Yup.string()
      .max(20)
      .label('Tax name')
      .when('taxId', {
        is: (name: string) => !!name,
        then: (schema) => schema.required('Required')
      }),
    ratePercent: Yup.number().min(0).max(50).label('Tax rate')

    // Solves the cyclic dependency issue
    // https://github.com/jquense/yup/issues/193#issuecomment-383712114
  },
  [['taxId', 'name']]
);

const INITIAL_VALUES = {
  taxId: '',
  name: '',
  ratePercent: 0
};

const TaxModal: React.FC<TaxModalProps> = ({
  initialValues,
  onClose,
  onSave
}) => {
  const { t } = useTranslation(['events']);

  return (
    <Formik
      initialValues={initialValues || INITIAL_VALUES}
      onSubmit={(values) => {
        onSave(values.taxId ? values : null);
        onClose();
      }}
      enableReinitialize
      validationSchema={TaxSchema}
    >
      {({ setValues, submitForm }) => (
        <Modal
          type={ModalType.SMALL}
          onClose={onClose}
          autoHeight
          visible
          icon={<MdRequestQuote size={20} />}
          title={t('events:settings.tax.modal.title')}
          renderFooter={() => (
            <Button type={ButtonType.Primary} onClick={submitForm}>
              {t('events:settings.tax.modal.saveButton')}
            </Button>
          )}
        >
          <div className="px-4 pt-6">
            <Field
              label="Tax ID"
              id="taxId"
              name="taxId"
              placeholder="e.g. 1234556RT0001"
              clearable
              onClear={() => {
                setValues(INITIAL_VALUES);
              }}
            />

            <div className="flex gap-4">
              <Field
                label={t('events:settings.tax.modal.taxNameLabel')}
                id="name"
                name="name"
                placeholder={t('events:settings.tax.modal.taxNamePlaceholder')}
              />

              <Field
                type="number"
                label={t('events:settings.tax.modal.taxRateLabel')}
                id="ratePercent"
                name="ratePercent"
                placeholder={t('events:settings.tax.modal.taxRatePlaceholder')}
              />
            </div>
          </div>
        </Modal>
      )}
    </Formik>
  );
};

export default TaxModal;
